<!-- 
 * 联盟收益计算器组件
 * 功能：帮助餐厅主计算加入联盟后的预期收益
 * 核心逻辑：每位顾客年消费 $3,600，50% 在联盟消费，餐厅获得 1% 收益 = $18/年/顾客
 -->
<template>
    <el-dialog v-model="dialogVisible" top="60px" :title="$t('calculator.title')" width="90%"
        :style="{ maxWidth: '600px' }" :close-on-click-modal="false" class="revenue-calculator-dialog"
        @close="handleClose">
        <div class="calculator-container">
            <!-- 提示信息区域 / 收益展示区域 - 共享空间 -->
            <div class="calculator-header">
                <div class="shared-area">
                    <!-- 未输入时显示提示信息 -->
                    <div v-if="!customerCount" class="warning-message">
                        <SvgIcon icon="heroicons:exclamation-triangle" class="warning-icon" />
                        <span class="warning-text">{{ $t('calculator.warning') }}</span>
                    </div>

                    <!-- 已输入时显示收益卡片 -->
                    <div v-if="customerCount > 0" class="result-card">
                        <div class="result-label">{{ $t('calculator.yearlyRevenue') }}</div>
                        <div class="result-value">
                            <span class="currency">$</span>
                            <span class="amount">{{ formattedRevenue }}</span>
                        </div>
                        <div class="result-hint">
                            <span>{{ $t('calculator.hint') }}</span>
                            <el-tooltip :content="$t('calculator.rules')" placement="top" effect="dark"
                                :show-arrow="true">
                                <div>
                                    <SvgIcon icon="heroicons:question-mark-circle" class="help-icon" />
                                </div>
                            </el-tooltip>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 计算器主体 -->
            <div class="calculator-body">
                <!-- 引导行动 -->
                <div class="cta-section">
                    <p class="cta-text">{{ $t('calculator.cta') }}</p>
                    <el-button type="primary" size="large" class="join-button" @click="handleJoinClick">
                        {{ $t('calculator.joinButton') }}
                    </el-button>
                </div>

                <!-- 输入区域 -->
                <div class="input-section">
                    <label class="input-label">{{ $t('calculator.inputLabel') }}</label>
                    <el-input v-model.number="customerCount" type="number" :min="0" :max="100000"
                        :placeholder="$t('calculator.placeholder')" size="large" class="customer-input"
                        @input="calculateRevenue">
                        <template #prefix>
                            <SvgIcon icon="heroicons:user-group" />
                        </template>
                    </el-input>
                </div>

                <!-- 收益梯度展示 - 始终显示 -->
                <div class="revenue-tiers">
                    <div class="tier-title">{{ $t('calculator.examples') }}</div>
                    <div class="tier-list">
                        <div v-for="tier in revenueTiers" :key="tier.customers" class="tier-item">
                            <span class="tier-customers">{{ tier.customers.toLocaleString() }} {{
                                $t('calculator.customers')
                                }}</span>
                            <span class="tier-arrow">→</span>
                            <span class="tier-revenue">${{ tier.revenue.toLocaleString() }}+</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import SvgIcon from '@/components/SvgIcon.vue';

interface Props {
    /** 对话框显示状态 */
    modelValue?: boolean;
}

interface Emits {
    (e: 'update:modelValue', value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: false
});

const emit = defineEmits<Emits>();
const router = useRouter();

// 对话框显示状态
const dialogVisible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
});

// 顾客数量
const customerCount = ref<number>(0);

// 年收益计算：每位顾客年消费 $3,600，50% 在联盟，餐厅获得 1% = $18
const REVENUE_PER_CUSTOMER = 18;

// 计算年收益
const yearlyRevenue = computed(() => {
    return customerCount.value * REVENUE_PER_CUSTOMER;
});

// 格式化收益显示
const formattedRevenue = computed(() => {
    return yearlyRevenue.value.toLocaleString();
});

// 收益梯度示例
const revenueTiers = [
    { customers: 1000, revenue: 18000 },
    { customers: 2000, revenue: 36000 },
    { customers: 5000, revenue: 90000 }
];

/**
 * 计算收益（输入变化时触发）
 */
const calculateRevenue = () => {
    // 确保输入值在合理范围内
    if (customerCount.value < 0) {
        customerCount.value = 0;
    }
    if (customerCount.value > 100000) {
        customerCount.value = 100000;
    }
};

/**
 * 关闭对话框
 */
const handleClose = () => {
    dialogVisible.value = false;
    // 延迟重置，避免关闭动画时看到数据变化
    setTimeout(() => {
        customerCount.value = 0;
    }, 300);
};

/**
 * 点击加入按钮，跳转到联系页面
 */
const handleJoinClick = () => {
    router.push('/contact');
    handleClose();
};
</script>

<style scoped lang="scss">
@use '@/styles/scss/typography.scss';
@use '@/styles/scss/mixins.scss';

.revenue-calculator-dialog {
    :deep(.el-dialog__header) {
        background: linear-gradient(135deg, #EC6937 0%, #1199FF 100%);
        color: white;
        padding: 20px 24px;
        margin: 0;
        border-radius: 8px 8px 0 0;
    }

    :deep(.el-dialog__title) {
        color: white;
        font-size: 20px;
        font-weight: 600;
    }

    :deep(.el-dialog__close) {
        color: white;
        font-size: 20px;

        &:hover {
            color: rgba(255, 255, 255, 0.8);
        }
    }

    :deep(.el-dialog__body) {
        padding: 24px;
    }
}

.calculator-container {
    .calculator-header {
        margin-bottom: 24px;

        // 共享区域 - 提示信息和收益卡片
        .shared-area {
            min-height: 70px;
            position: relative;


            .warning-message {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 12px 16px;
                background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
                border-left: 4px solid #EC6937;
                border-radius: 4px;

                .warning-icon {
                    font-size: 20px;
                    color: #EC6937;
                    flex-shrink: 0;
                }

                .warning-text {
                    flex: 1;
                    font-size: 14px;
                    color: #000000;
                    line-height: 1.5;
                }
            }

            .result-card {
                padding: 24px;
                background: linear-gradient(135deg, #EC6937 0%, #1199FF 100%);
                border-radius: 12px;
                text-align: center;
                color: white;
                box-shadow: 0 4px 12px rgba(236, 105, 55, 0.3);

                .result-label {
                    font-size: 14px;
                    opacity: 0.9;
                    margin-bottom: 8px;
                }

                .result-value {
                    font-size: 48px;
                    font-weight: 700;
                    line-height: 1.2;
                    margin-bottom: 8px;

                    .currency {
                        font-size: 32px;
                        margin-right: 4px;
                    }

                    .amount {
                        animation: countUp 0.5s ease-out;
                    }
                }

                .result-hint {
                    font-size: 12px;
                    opacity: 0.8;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;

                    .help-icon {
                        font-size: 16px;
                        cursor: help;
                        transition: transform 0.3s ease;

                        &:hover {
                            transform: scale(1.1);
                        }
                    }
                }
            }
        }
    }

    .calculator-body {
        .input-section {
            margin-bottom: 24px;

            .input-label {
                display: block;
                margin-bottom: 12px;
                font-size: 16px;
                font-weight: 500;
                color: #000000;
            }

            .customer-input {
                :deep(.el-input__inner) {
                    font-size: 18px;
                    text-align: center;
                }
            }
        }

        .revenue-tiers {
            .tier-title {
                font-size: 14px;
                font-weight: 500;
                color: #000000;
                margin-bottom: 12px;
            }

            .tier-list {
                display: flex;
                flex-direction: column;
                gap: 10px;

                .tier-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 16px;
                    background: #f5f7fa;
                    border-radius: 8px;
                    font-size: 14px;

                    .tier-customers {
                        color: #000000;
                        font-weight: 500;
                    }

                    .tier-arrow {
                        color: #909399;
                    }

                    .tier-revenue {
                        color: #1199FF;
                        font-weight: 600;
                    }
                }
            }
        }

        .cta-section {
            text-align: center;
            padding-top: 16px;
            border-top: 1px solid #e5e7eb;

            .cta-text {
                font-size: 14px;
                color: #000000;
                margin-bottom: 16px;
                line-height: 1.6;
            }

            .join-button {
                width: 100%;
                max-width: 300px;
                height: 48px;
                font-size: 16px;
                font-weight: 600;
                background: linear-gradient(135deg, #EC6937 0%, #1199FF 100%);
                border: none;

                &:hover {
                    opacity: 0.9;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(236, 105, 55, 0.4);
                }
            }
        }
    }
}

@keyframes countUp {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// 淡入淡出动画
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* 移动端适配 */
@media (max-width: 768px) {
    .revenue-calculator-dialog {
        :deep(.el-dialog__header) {
            padding: 16px 20px;
        }

        :deep(.el-dialog__title) {
            font-size: 18px;
        }

        :deep(.el-dialog__body) {
            padding: 20px;
        }
    }

    .calculator-container {
        .calculator-header {
            .shared-area {
                min-height: 140px;

                .result-card {
                    padding: 20px;

                    .result-value {
                        font-size: 36px;

                        .currency {
                            font-size: 24px;
                        }
                    }
                }
            }
        }

        .calculator-body {
            .revenue-tiers {
                .tier-list {
                    .tier-item {
                        font-size: 13px;
                        padding: 10px 12px;
                    }
                }
            }

            .cta-section {
                .join-button {
                    height: 44px;
                    font-size: 15px;
                }
            }
        }
    }
}
</style>
